import { Goal } from 'modules/goals/types';
import { useQuery, UseQueryOptions } from 'react-query';
import { ApiError, ApiRequest } from 'services/api';
import { RestQuery } from 'utils/hooks/api/types';
import { RestQueryHookOptions } from 'utils/hooks/api/useRestQuery';
import { calculateRetryDelay, POST_QUERY } from 'utils/hooks/api/utils';

export const MISSION_CONTROL_PROJECT_GOALS_URL = '/i/goals/search';

type QueryProjectGoalsResponse = {
  goals: Goal[];
  total: number;
};

export type UseQueryProjectGoalsState = ReturnType<typeof useQueryProjectGoals>;

const createQueryHookOptions = <Response, Params = Record<string, unknown>>(
  query: RestQuery<Response, Params>,
): UseQueryOptions<Response | undefined, ApiError> => {
  const { url, params, queryOptions } = query;
  const queryFn = POST_QUERY(query);

  return {
    queryKey: [url, params],
    queryFn,
    retryDelay: calculateRetryDelay,
    ...queryOptions,
  };
};

/**
 * NOTE: this is currently making a POST request, we should work with BE on refactoring this to use a GET endpoint instead to make it easier to integrate with our FE infrastructure.
 */
export const useQueryProjectGoals = <
  Response extends QueryProjectGoalsResponse,
  Params extends Record<string, unknown> = Record<string, unknown>,
>(
  params: Params,
  queryOptions?: RestQueryHookOptions<Response>,
  requestConfig?: ApiRequest,
) => {
  const {
    data,
    isLoading,
    error: errorMessage,
    isError: hasError,
    refetch,
  } = useQuery(
    createQueryHookOptions({
      url: MISSION_CONTROL_PROJECT_GOALS_URL,
      params,
      queryOptions,
      requestConfig,
    }),
  );

  return {
    total: (data as QueryProjectGoalsResponse)?.total || 0,
    projectGoals: (data as QueryProjectGoalsResponse)?.goals || [],
    isLoading,
    hasError,
    errorMessage,
    refetch,
  };
};
